'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { FaUserGroup, FaRulerCombined, FaBed } from 'react-icons/fa6';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

type RealtyImage = {
  id: number;
  name: string;
  url: string;
  description: string | null;
  custom_title: string | null;
  metadata: unknown;
};

type Realty = {
  id: number;
  name: string;
  slug: string;
  image: RealtyImage | null;
  images: RealtyImage[];
  price: number;
  realty_area: number | null;
};

export const RoomCard = ({ realty }: { realty: Realty }) => {
  const { t } = useTranslations();
  const cover = realty.image?.url || realty.images?.[0]?.url || '/images/placeholder.png';

  const personValue = realty.attributes.find(v => v.id === 1)?.values?.[0]?.name || '3';
  const bedsValue = realty.attributes.find(v => v.id === 2)?.values?.[0]?.name || 'Viac lôžok';
  const equipmentValues = realty.attributes.find(v => v.id === 3)?.values;
  const extraInfoValues = realty.attributes.find(v => v.id === 4)?.values;

  return (
    <article className="grid grid-cols-1 overflow-hidden rounded-xl border border-border bg-background shadow-sm sm:grid-cols-[320px,1fr]">
      <div className="relative aspect-[4/3] w-full sm:aspect-auto sm:h-full">
        <Image
          src={cover}
          alt={resolveImageAlt((realty?.image?.url ? realty?.image : realty?.images?.[0]), realty?.name)}
          fill
          className="object-cover"
        />
      </div>
      <div className="flex flex-col gap-3 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Link href={`/${realty.slug}`}>
            <h3 className="text-xl font-semibold">{realty.name}</h3>
          </Link>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <FaUserGroup className="h-4 w-4" /> {personValue} max
          </div>
          {realty.realty_area && (
            <div className="flex items-center gap-2">
              <FaRulerCombined className="h-4 w-4" /> {realty.realty_area ?? 20} m²
            </div>
          )}
          <div className="flex items-center gap-2">
            <FaBed className="h-4 w-4" /> {bedsValue}
          </div>
        </div>
        <HtmlRenderer
          html={realty.description || ''}
          className="line-clamp-3 text-muted-foreground [&_p]:text-sm"
        />
        {equipmentValues?.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {(() => {
              const names = equipmentValues.map(v => v.name);
              const shown = names.slice(0, 3);
              const remaining = names.length - shown.length;
              return `${shown.join(' • ')}${remaining > 0 ? ` • ${remaining} iných zariadení` : ''}`;
            })()}
          </div>
        )}
        <div className="mt-auto grid grid-cols-1 items-center gap-3 sm:grid-cols-2">
          <div className="gap-2 text-sm">
            <div className="flex items-center gap-2">
              <p className="text-muted-foreground">{t('text3617')}</p>
              <p className="text-lg font-semibold">{realty.price} €/noc</p>
            </div>
            <p className="text-xs text-muted-foreground">Vylúčené dane a poplatky: 3 €/noc</p>
          </div>
          <div className="flex w-full justify-end sm:justify-end">
            <Link
              href={`/${realty.slug}`}
              className="inline-flex h-12 w-full items-center justify-center rounded-md bg-primary px-8 font-semibold text-primary-foreground hover:opacity-90 sm:w-auto sm:text-lg"
            >
              {t('text2076')}
            </Link>
          </div>
        </div>
        {extraInfoValues?.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {extraInfoValues.map(v => v.name).join(' • ')}
          </div>
        )}
      </div>
    </article>
  );
};

export default RoomCard;
